import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import isEmpty from 'lodash/isEmpty';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  addActivity,
  addEvent,
  logout,
  openContactSupportDrawer,
  openNotificationsDrawer,
  selectNotificationBellCount
} from '@capital-access/common/actions';
import { PendoAccessService } from '@capital-access/common/auth';
import { CookieSettingsService, SelectorSecurity } from '@capital-access/common/components';
import { getFeatureToggle } from '@capital-access/common/feature-toggle';
import { LocalizationService } from '@capital-access/common/localization';
import { ToggleNavItemConfig, UserProfileDropdownItem } from '@capital-access/common/navigation';
import {
  getPrimarySecuritySettings,
  SecuritySelectorProvider,
  setSelectedSecurityId
} from '@capital-access/common/security';
import {
  getAccountPreferences,
  getEntitlements,
  getUserLoadStatus,
  REPORT_ACCESS_ONLY_PREFERENCE_KEY,
  REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE,
  UserRepository
} from '@capital-access/common/user';
import {
  CA_CONSULTANT_IMPERSONATION_ENTITLEMENT,
  getAccount,
  selectIsImpersonating
} from '@capital-access/common/user';
import { mapToBnyReportOnlyAccessRestriction, mapToRecord } from '@capital-access/common/utils';
import { getBrandingSettings } from '@capital-access/theming';
import { EntityItem } from '../../models/entity-item';
import { EventItem } from '../../models/event-item';
import {
  CONTACT_SUPPORT_URL,
  COOKIE_SETTINGS_URL,
  LOGOUT_URL,
  NAVIGATION_LOCALIZATION_SCOPE
} from '../navigation.constants';
import { SharedNavigationItemsService } from '../services/ca-shared-navigation-items.service';

@Component({
  selector: 'ca-top-nav',
  templateUrl: './top-nav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TopNavComponent {
  @Input() flexibleTopNav = false;

  user$ = this.sharedNavigationItemsService.user$;
  userSettingsOptions$: Observable<UserProfileDropdownItem[]> =
    this.sharedNavigationItemsService.userSettingsOptionsTop$;

  showApplicationTitle$ = this.store.select(getBrandingSettings).pipe(map(({ brand }) => !(brand === 'bny')));
  isImpersonating$ = this.store.select(selectIsImpersonating);

  shouldHideComponents$: Observable<boolean>;

  shouldHideFeedbackButton$: Observable<boolean>;

  hasCAConsultantEntitlement$: Observable<boolean>;

  hasConsultantClientAccountSwitcherFT$: Observable<boolean>;

  showConsultantSelector$: Observable<boolean>;

  isConsultantClientsAvailable = false;

  toggleSettings: ToggleNavItemConfig = this.sharedNavigationItemsService.toggleSettingsOption[0];

  entityIcon: string = this.sharedNavigationItemsService.entitySettingsIcon;
  entityList: EntityItem[] = this.sharedNavigationItemsService.entitySettingsOptionsTop;

  eventIcon = 'f-i-calendar-add';
  eventList: EventItem[] = [
    { localizationKey: 'newActivityTooltip', action: addActivity() },
    { localizationKey: 'newEventTooltip', action: addEvent() }
  ];

  isConsultantAccountType$: Observable<boolean>;

  isConsultantAccountClientsAvailable$: Observable<boolean>;

  showSecuritySelector$: Observable<boolean>;

  isOnlyOneAvailableSecurities$: Observable<boolean>;

  shouldShowAlertsIcon = this.store.selectSignal(getFeatureToggle('CA-Alerts'));
  enableNotificationCenter = this.store.selectSignal(getFeatureToggle('notification-center'));
  notificationBellCount = this.store.selectSignal(selectNotificationBellCount);

  applicationSecurities$ = this.provider.getSecurities().pipe(
    switchMap(availableSecurities =>
      this.store.select(getPrimarySecuritySettings).pipe(
        withLatestFrom(
          this.localizationService.localize(
            ['availableSecurities', 'pinnedSecurities'],
            {},
            NAVIGATION_LOCALIZATION_SCOPE
          )
        ),
        map(([primarySecurities, [availableSecuritiesTitle, pinnedSecuritiesTitle]]) => {
          const all = mapToRecord(availableSecurities, x => x.id);
          const primary = primarySecurities.primary?.filter(id => !!all[id]).map(x => all[x]) ?? [];

          const availableSecurityItems: SelectorSecurity[] = availableSecurities.map(x => {
            return { ...x, category: availableSecuritiesTitle };
          });

          const pinnedSecurityItems: SelectorSecurity[] = primary.map(x => {
            return { ...x, category: pinnedSecuritiesTitle };
          });

          if (isEmpty(pinnedSecurityItems) && availableSecurityItems.length > 5) {
            pinnedSecurityItems.push({
              id: null,
              name: '',
              ticker: '',
              category: pinnedSecuritiesTitle
            } as unknown as SelectorSecurity);
          }
          return availableSecurityItems.length > 5
            ? [...pinnedSecurityItems, ...availableSecurityItems]
            : availableSecurityItems;
        })
      )
    )
  );

  applicationSelectedSecurity$ = this.provider.getSelectedSecurity();

  constructor(
    private router: Router,
    private store: Store,
    private localizationService: LocalizationService,
    private sharedNavigationItemsService: SharedNavigationItemsService,
    private provider: SecuritySelectorProvider,
    private cookieSettingsService: CookieSettingsService,
    private pendoAccessService: PendoAccessService,
    private userRepository: UserRepository
  ) {
    const userStatus$ = this.store.select(getUserLoadStatus);
    const preferences$ = this.store.select(getAccountPreferences);
    const featureToggle$ = this.store.select(getFeatureToggle(REPORT_ACCESS_RESTRICTION_FEATURE_TOGGLE));

    this.shouldHideComponents$ = combineLatest([userStatus$, preferences$, featureToggle$]).pipe(
      mapToBnyReportOnlyAccessRestriction(REPORT_ACCESS_ONLY_PREFERENCE_KEY)
    );

    this.hasConsultantClientAccountSwitcherFT$ = this.store.select(
      getFeatureToggle('consultant-client-account-switcher-ISD-16483')
    );
    this.hasCAConsultantEntitlement$ = this.store
      .select(getEntitlements)
      .pipe(
        map(
          entitlements =>
            entitlements?.functions.some(f => f.sourceId === CA_CONSULTANT_IMPERSONATION_ENTITLEMENT) ?? false
        )
      );

    this.shouldHideFeedbackButton$ = this.pendoAccessService.checkAccess().pipe(map(result => result.shouldHidePendo));

    this.isConsultantAccountType$ = this.store
      .select(getAccount)
      .pipe(map(account => account?.accountSubType === 'Consultant'));

    this.isConsultantAccountClientsAvailable$ = this.isConsultantAccountType$.pipe(
      switchMap(isConsultant => {
        if (isConsultant) {
          return this.userRepository
            .getConsultantClients()
            .pipe(map(clients => Array.isArray(clients) && clients.length > 0));
        }
        return of(false);
      })
    );

    this.showConsultantSelector$ = combineLatest([
      this.hasConsultantClientAccountSwitcherFT$,
      this.hasCAConsultantEntitlement$,
      this.isConsultantAccountClientsAvailable$
    ]).pipe(
      map(([hasSwitcher, hasEntitlement, hasClients]) => hasSwitcher && hasEntitlement && hasClients),
      shareReplay(1)
    );

    this.isOnlyOneAvailableSecurities$ = this.applicationSecurities$.pipe(
      map(securities => Array.isArray(securities) && securities.length < 2)
    );

    this.showSecuritySelector$ = combineLatest([
      this.shouldHideComponents$,
      this.hasConsultantClientAccountSwitcherFT$,
      this.isConsultantAccountType$,
      this.isOnlyOneAvailableSecurities$
    ]).pipe(
      map(
        ([shouldHide, hasSwitcher, isConsultant, isOnlyOne]) =>
          !shouldHide && !(hasSwitcher && isConsultant && isOnlyOne)
      )
    );
  }

  onSetSecurities() {
    this.router.navigate(['user', 'settings']);
  }

  onApplicationSecuritySelected(selectedSecurity: { id: number }) {
    this.store.dispatch(setSelectedSecurityId({ selectedSecurityId: selectedSecurity.id }));
  }

  onSurveillanceToggle() {
    this.store.dispatch(this.toggleSettings.action);
  }

  entityItemClick(entityIndex: number): void {
    this.store.dispatch(this.entityList[entityIndex].action);
  }

  eventItemClick(eventIndex: number): void {
    this.store.dispatch(this.eventList[eventIndex].action);
  }

  userItemClick($event: unknown): void {
    const item = $event as UserProfileDropdownItem;

    if (item.route === CONTACT_SUPPORT_URL) {
      this.store.dispatch(openContactSupportDrawer());
    } else if (item.route === LOGOUT_URL) {
      this.store.dispatch(logout());
    } else if (item.route === COOKIE_SETTINGS_URL) {
      this.cookieSettingsService.showCookieSettings();
    } else {
      this.router.navigate([item.route]);
    }
  }

  openPendoPortal(): void {
    this.router.navigate(['pendo-portal']);
  }

  logout() {
    this.store.dispatch(logout());
  }

  openNotificationsPanel(): void {
    if (this.enableNotificationCenter()) {
      this.store.dispatch(openNotificationsDrawer());
    } else {
      this.router.navigateByUrl('/data-alerts');
    }
  }
}
